import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

// Define the theme types
export type ThemeMode = "standard" | "accessibility";

// Define the colors interface
export interface ThemeColors {
  primaryGreen: string;
  secondaryGreen: string;
  accentBlue: string;
  primaryGreenLighter: string;
  primaryGreenLightest: string;
  secondaryGreenLighter: string;
  secondaryGreenLightest: string;
  accentBlueLighter: string;
  accentBlueLightest: string;
  textColor: string;
  textColorMuted: string;
  warningColor: string;
  errorColor: string;
  linkHover: string;
  warningTextColor: string;
  primaryTextColor: string;
  warningBackgroundColor: string;
  darkGreenColor: string;
}

// Define theme values for each mode
export const themeValues: Record<ThemeMode, ThemeColors> = {
  standard: {
    primaryGreen: "#53B741",
    secondaryGreen: "#00A550",
    accentBlue: "#6364F6",
    primaryGreenLighter: "#7ece6f", // 20% lighter
    primaryGreenLightest: "#E5F6DA", // 30% lighter
    secondaryGreenLighter: "#00c967", // 20% lighter
    secondaryGreenLightest: "#4dffb0", // 30% lighter
    accentBlueLighter: "#9293f9", // 20% lighter
    accentBlueLightest: "#c1c1fb", // 30% lighter
    textColor: "#40424c",
    textColorMuted: "#858383",
    warningColor: "#E67300",
    warningBackgroundColor: "#ff7f1c",
    errorColor: "#db2828",
    linkHover: "#1e2139b3",
    warningTextColor: "#FFFFFF",
    primaryTextColor: "#000000",
    darkGreenColor: "#023A00",
  },
  accessibility: {
    primaryGreen: "#04884B",
    secondaryGreen: "#04884B",
    accentBlue: "#6464F2",
    primaryGreenLighter: "#05a65d", // 20% lighter
    primaryGreenLightest: "#E5F6DA", // 30% lighter
    secondaryGreenLighter: "#05a65d", // 20% lighter
    secondaryGreenLightest: "#06c470", // 30% lighter
    accentBlueLighter: "#9393f5", // 20% lighter
    accentBlueLightest: "#c2c2f9", // 30% lighter
    textColor: "#333333", // Higher contrast text
    textColorMuted: "#595959", // Higher contrast muted text
    warningColor: "#C35600", // Higher contrast warning color
    errorColor: "#C41E3A", // Higher contrast error color
    linkHover: "#0C4D8A", // Higher contrast link hover
    warningTextColor: "#023A00",
    darkGreenColor: "#023A00",
    primaryTextColor: "#FFFFFF",
    warningBackgroundColor: "#ff7f1c",
  },
};

interface ThemeContextType {
  themeMode: ThemeMode;
  toggleTheme: () => void;
  colors: ThemeColors;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize from localStorage if available, otherwise default to standard
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const savedTheme = localStorage.getItem("themeMode");
    return (savedTheme as ThemeMode) || "accessibility";
  });

  // Update the theme in localStorage when it changes
  useEffect(() => {
    localStorage.setItem("themeMode", themeMode);

    // Apply the theme to CSS variables
    const root = document.documentElement;

    // Set base colors
    root.style.setProperty("--primary-green", themeValues[themeMode].primaryGreen);
    root.style.setProperty("--secondary-green", themeValues[themeMode].secondaryGreen);
    root.style.setProperty("--accent-blue", themeValues[themeMode].accentBlue);
    root.style.setProperty("--warning-text-color", themeValues[themeMode].warningTextColor);
    root.style.setProperty("--primary-text-color", themeValues[themeMode].primaryTextColor);
    root.style.setProperty("--warning-background-color", themeValues[themeMode].warningBackgroundColor);
    root.style.setProperty("--dark-green-color", themeValues[themeMode].darkGreenColor);

    // Set lighter variations
    root.style.setProperty("--primary-green-lighter", themeValues[themeMode].primaryGreenLighter);
    root.style.setProperty("--primary-green-lightest", themeValues[themeMode].primaryGreenLightest);
    root.style.setProperty("--secondary-green-lighter", themeValues[themeMode].secondaryGreenLighter);
    root.style.setProperty("--secondary-green-lightest", themeValues[themeMode].secondaryGreenLightest);
    root.style.setProperty("--accent-blue-lighter", themeValues[themeMode].accentBlueLighter);
    root.style.setProperty("--accent-blue-lightest", themeValues[themeMode].accentBlueLightest);

    // Set common UI colors
    root.style.setProperty("--text-color", themeValues[themeMode].textColor);
    root.style.setProperty("--text-color-muted", themeValues[themeMode].textColorMuted);
    root.style.setProperty("--warning-color", themeValues[themeMode].warningColor);
    root.style.setProperty("--error-color", themeValues[themeMode].errorColor);
    root.style.setProperty("--link-hover", themeValues[themeMode].linkHover);

    // Set additional UI colors
    root.style.setProperty("--background-color", "#ffffff");
    root.style.setProperty("--disabled-bg", themeMode === "standard" ? "#e0e1e2" : "#d0d0d0");
    root.style.setProperty("--disabled-text", themeMode === "standard" ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.7)");
    root.style.setProperty("--light-gray-bg", themeMode === "standard" ? "#efefef" : "#e6e6e6");
    root.style.setProperty("--form-border", themeMode === "standard" ? "#d9dade" : "#b5b5b5");
    root.style.setProperty("--form-label", themeMode === "standard" ? "#49454f" : "#333333");
  }, [themeMode]);

  const toggleTheme = () => {
    setThemeMode((prev) => (prev === "standard" ? "accessibility" : "standard"));
  };

  const value = {
    themeMode,
    toggleTheme,
    colors: themeValues[themeMode],
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
